import {BuyButton} from '@/domains/panier/BuyProduct/BuyButton';
import {BuyProductPanierInput} from '@/domains/panier/BuyProduct/BuyProductPanierInput';
import {ItemCatalogue2Desc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import {Box, StyleProps, VStack, useBreakpointValue} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {
  isItemCatalogueMarketplace,
  useLivraisonsPossiblesContext,
} from 'ff-common';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';
import {useAjouterAuPanierMarketplace} from '@/hooks/livraison/use-ajouter-au-panier-marketplace';
import {CatalogueMarketplace} from 'api-types/ffconnect';
import {useLigneDePanierCommands} from 'ff-common';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {ContextsComposant} from 'ff-common/src/types/ContextsComposant';

export const MarketplaceBuyButton: React.FC<
  {
    itemState: State<ItemCatalogue2Desc>;
    ligneDePanier?: State<LigneDePanierDesc>;
    marketplaceLigneDePanier?: State<LigneDePanierDesc>;
    context?: ContextsComposant;
  } & StyleProps
> = ({
  itemState,
  ligneDePanier,
  marketplaceLigneDePanier,
  context = 'catalogue',
  ...props
}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();

  const isMarketplace = isItemCatalogueMarketplace(itemState.data);
  const marketplace_id: CatalogueMarketplace = itemState.data.marketplace_id;
  const {couleur, textCouleur} = useMarketplaceBrandColors(marketplace_id);
  const primaryColor = couleur ?? 'black.ff';
  const primaryTextColor = textCouleur ?? 'white';
  const iconColor =
    useBreakpointValue({base: primaryTextColor, md: primaryColor}) ??
    primaryColor;

  const {submit, isSubmitting} = useAjouterAuPanierMarketplace(textToast);

  const onError = useDefaultOnError(textToast);
  const {setSelectedMarketplaceId} = useLivraisonsPossiblesContext();
  const effectiveLigneDePanier = marketplaceLigneDePanier ?? ligneDePanier;
  const {changerLaQuantite, somethingSubmitting} = useLigneDePanierCommands(
    effectiveLigneDePanier,
    textToast,
    onError,
    () => null,
    itemState.data.multiple_de_commande_client,
    undefined,
    undefined,
    {
      onChangerLaQuantiteSuccess: () => {
        setSelectedMarketplaceId(marketplace_id);
      },
    },
  );

  if (!isMarketplace) {
    return null;
  }

  const onClick = () => {
    submit(
      {
        code_article: itemState.data.code,
        quantite: itemState.data.minimum_de_commande_client || 1,
        commande_en: 'unitaire',
        marketplace_id,
      },
      itemState,
    );
  };

  const buyButtonProps = {
    libelle: t('bouton_achat.buy'),
    onClick,
    isSubmitting,
    bg: {base: primaryColor, md: 'white'},
    borderColor: primaryColor,
    color: {base: primaryTextColor, md: primaryColor},
    iconColor,
    _hover: {
      bg: {base: 'gray.800', md: 'gray.light'},
      borderColor: primaryColor,
    },
    minH: '35px',
    width: '100%',
  };

  return (
    <VStack
      justifyContent={'end'}
      mt='15px'
      w='100%'
      minW={0}
      position='relative'
      {...props}
    >
      {effectiveLigneDePanier && effectiveLigneDePanier.data.quantite > 0 ? (
        <Box
          width='100%'
          bg='white'
          border='0.5px'
          borderColor='gray.200'
          borderRadius='8px'
        >
          <BuyProductPanierInput
            itemCatalogue={itemState}
            quantite={effectiveLigneDePanier.data.quantite}
            commande_en='unitaire'
            somethingSubmitting={somethingSubmitting}
            context={context}
            primaryColor={couleur}
            onQuantite={(q) => {
              changerLaQuantite.submit({quantite: q});
            }}
          />
        </Box>
      ) : (
        <BuyButton {...buyButtonProps} />
      )}
    </VStack>
  );
};
